import { gql } from '@apollo/client'
import { VideoDetail, LeadThumbnailImage } from '@fs/zion-rootstech'
import { brightspotConfig } from '../../config'
import { escapeBackslash } from '../helpers'
import VideoMutation from './fragments/VideoMutation'

const { defaults } = brightspotConfig.query

// Targeted lookup for contexts that render a video's thumbnail at hero/poster scale (e.g.
// MediaPromo) instead of card scale. ConferencePromo/VideoModule's shared `video` field stays
// on the small, card-sized ThumbnailImage because it's also spread into swipeable card grids
// (calendar carousels, session search, exhibitor promo lists) within the same query — GraphQL
// can't give two array items of the same type two different image sizes. This fetches just the
// large crop for the one video a hero-scale component is actually rendering.
//
// Parsed lazily and cached on first call (rather than eagerly at module scope) so
// useLeadVideoThumbnail, which calls this on every render, hands useQuery a stable DocumentNode
// after the first call instead of a fresh one each time. Staying lazy also matters here
// specifically: some test files replace the entire @fs/zion-rootstech module with a narrow
// jest.mock() factory that only covers the exports that test's own render path needs. Parsing
// eagerly at module scope would run gql() (and dereference LeadThumbnailImage) the moment
// anything merely imports this file — even tests that never call getVideoLeadThumbnail — so it
// would crash under any such narrow mock that doesn't happen to include LeadThumbnailImage.
let videoLeadThumbnailQuery
export function getVideoLeadThumbnail() {
  if (!videoLeadThumbnailQuery) {
    videoLeadThumbnailQuery = gql(`
      ${LeadThumbnailImage}
      query VideoLeadThumbnail(
        $id: ID,
        $leadThumbnailImage_crop: Boolean = false,
        $leadThumbnailImage_height: Int = ${defaults.imageSize},
        $leadThumbnailImage_width: Int = ${defaults.imageSize}) {
        VideoDetail(id: $id) {
          thumbnail {
            ...LeadThumbnailImage
          }
        }
      }
    `)
  }
  return videoLeadThumbnailQuery
}

function mutateVideo() {
  return gql(`
  ${VideoMutation}
  mutation MutateVideo(
    $creators: [ID],
    $exhibitors: [ID],
    $headline: String,
    $subHeadline: String,
    $contentLocale: String,
    $target: MutationTargetInput,
    $thumbnail: ID,
    $thumbnailImage_crop: Boolean = false,
    $thumbnailImage_height: Int = ${defaults.imageSize},
    $thumbnailImage_width: Int = ${defaults.imageSize},
    $videoProvider: VideoProviderInput) {
      VideoMutation(
        _target: $target,
        creators: $creators,
        exhibitors: $exhibitors,
        headline: $headline,
        subHeadline: $subHeadline,
        thumbnail: $thumbnail,
        contentLocale: $contentLocale
        videoProvider: $videoProvider ) {
          ...VideoMutation
      }
  }
  `)
}

function deleteVideoMutation() {
  return gql(`
    ${VideoDetail}
    mutation MutateVideo(
      $creators: [ID],
      $exhibitors: [ID],
      $target: MutationTargetInput,
      $videoProvider: VideoProviderInput
      $thumbnailImage_crop: Boolean = false,
      $thumbnailImage_height: Int = ${defaults.imageSize},
      $thumbnailImage_width: Int = ${defaults.imageSize},
      $workflow: WorkflowInput = { action: DELETE }) {
      VideoMutation(
        _target: $target, 
        creators: $creators,
        exhibitors: $exhibitors,
        videoProvider: $videoProvider
        _workflow: $workflow) {
        result {
          ...VideoDetail
        }
      }
    }
  `)
}

// export async function getVideo(client, videoId, locale) {
//   const variables = {
//     target: { id: videoId, locale },
//   }
//   const response = await client.mutate({ mutation: getVideoMutation(), variables })
//   return response?.data?.VideoMutation?.result
// }

export async function createVideo(client, video) {
  const variables = {
    headline: escapeBackslash(video.headline),
    subHeadline: escapeBackslash(video.subHeadline),
    thumbnail: video.videoThumbnailId,
  }
  if (video.creatorId) {
    variables.creators = video.creatorId
  }
  if (video.exhibitorId) {
    variables.exhibitors = video.exhibitorId
  }
  // URL for YT or Webinar
  if (video.identifier && video.identifier !== 'placeholder') {
    // YouTube URL or actual live session URL
    variables.videoProvider = { identifier: video.identifier, provider: video.provider }
  } else if (video.provider === 'WEBINAR') {
    // Live session link will be added by RootsTech staff later
    variables.videoProvider = { identifier: 'placeholder', provider: video.provider }
  }
  // If we have a session id we are doing an update.  Otherwise, it is a create
  if (video.id) {
    variables.target = { id: video.id, locale: video.locale }
  } else {
    variables.contentLocale = video.locale ? video.locale : ''
  }
  const response = await client.mutate({ mutation: mutateVideo(), variables })
  return response?.data?.VideoMutation?.result
}

export async function updateVideo(client, video) {
  return createVideo(client, video)
}

export async function deleteVideo(client, video) {
  const variables = {
    target: { id: video.id, locale: video.locale },
  }
  if (video.creatorId) {
    variables.creators = video.creatorId
  }
  if (video.exhibitorId) {
    variables.exhibitors = video.exhibitorId
  }
  if (video.identifier) {
    variables.videoProvider = { identifier: video.identifier, provider: video.provider }
  }

  const response = await client.mutate({ mutation: deleteVideoMutation(), variables })
  return response?.data?.VideoMutation?.result
}
